import { MutationCache, QueryCache, QueryClient, type QueryKey } from '@tanstack/react-query';

import {
  isAppError,
  REFRESH_FAILED_CODE,
  toAppError,
  type AppError,
  type AppErrorKind,
} from '@cenorion/api';
import { toast } from '@cenorion/ui/components/sonner';

import { reportError } from '@/core/errors/report-error';
import { i18n } from '@/core/i18n';

// Module augmentation, not a bespoke `meta` type: this is TanStack Query's
// documented `Register` seam for typing `meta`, so `query.meta?.silent` and
// `mutation.meta?.silent` are booleans everywhere instead of `unknown`.
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { silent?: boolean };
    mutationMeta: { silent?: boolean };
  }
}

// Retry policy branches on `AppError.kind` rather than retrying blindly: a blanket `retry: 1`
// re-fires terminal failures after logout, retries 403s that will never succeed, and can
// double-submit a rejected mutation. The toast/report policy below routes on the same `kind`, so
// retry and error handling agree.
const TERMINAL_KINDS = new Set(['auth', 'forbidden', 'notFound', 'validation', 'canceled']);

/** Kinds with a dedicated owner elsewhere; never toast. */
const OWNED_ELSEWHERE: ReadonlySet<AppErrorKind> = new Set(['validation', 'canceled']);

/** Kinds worth a Sentry capture — the ones that indicate an actual defect. */
const REPORTED_KINDS: ReadonlySet<AppErrorKind> = new Set(['server', 'unknown']);

/** `notFound`/`forbidden` collide with the nested page-copy keys in translations.json. */
const MESSAGE_KEYS: Partial<Record<AppErrorKind, string>> = {
  notFound: 'NotFoundError',
  forbidden: 'ForbiddenError',
};

// `i18n.t` resolved lazily, inside the handler, never captured at module scope: this module
// evaluates at app boot, which can precede i18n init, and a `t` closed over at import time would
// keep returning raw keys for every error firing afterward.
function toastMessage(error: AppError): string {
  const key = MESSAGE_KEYS[error.kind] ?? capitalizeKind(error.kind);
  return i18n.t(`Error.${key}`, { defaultValue: error.message });
}

/** `AppErrorKind` values are camelCase; i18n key segments are PascalCase. */
function capitalizeKind(kind: AppErrorKind): string {
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

// The single Query v5 error policy point: `QueryCache`/`MutationCache`, since v5 removed per-hook
// `onError` for queries. `auth`/`validation`/`canceled` stay silent (the refresh path is
// mid-redirect, validation maps to form fields, a cancel is not a failure); `forbidden` is silent
// only for a query — the ability-loader's 403 page owns it — a mutation still toasts.
function handleRequestError(
  error: unknown,
  source: 'query' | 'mutation',
  queryKey?: QueryKey,
): void {
  // Normalizes rather than bails out: a throw inside `select`, a hand-written `queryFn`, or an
  // AppError that crossed a realm boundary all arrive un-normalized, and an `isAppError` early
  // return would swallow them completely — no toast, no Sentry, no trace id.
  const appError = toAppError(error);
  if (OWNED_ELSEWHERE.has(appError.kind)) return;
  if (appError.kind === 'auth') {
    // Silent either way: a dead session is mid-redirect and already reported by endSession. Any
    // other 401 arrives while the session is still live, so the backend rejected a token this
    // client considers valid — a server-side fault worth a capture, not a toast.
    if (appError.code !== REFRESH_FAILED_CODE) {
      reportError(appError, { errorKind: 'auth_error_with_live_session' });
    }
    return;
  }
  if (appError.kind === 'forbidden' && source === 'query') return;

  if (REPORTED_KINDS.has(appError.kind)) reportError(appError);

  const message = toastMessage(appError);

  if (queryKey && (appError.kind === 'network' || appError.kind === 'timeout')) {
    toast.error(message, {
      action: {
        label: i18n.t('Error.Retry', { defaultValue: 'Retry' }),
        onClick: () => void queryClient.refetchQueries({ queryKey }),
      },
    });
    return;
  }

  toast.error(message);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Background/polling queries opt out entirely so they never spam toasts for a failure
      // the UI tolerates silently.
      if (query.meta?.silent) return;
      handleRequestError(error, 'query', query.queryKey);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.silent) return;
      handleRequestError(error, 'mutation');
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      // Background refetch during data entry is disruptive in an admin tool.
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (isAppError(error) && TERMINAL_KINDS.has(error.kind)) return false;
        return failureCount < 1;
      },
      // A forbidden query throws to the route error boundary, which renders the 403 screen. Without
      // this the error sits in query state and the component renders blank — a route with no ability
      // loader has nothing else to catch a server denial the client did not predict.
      throwOnError: (error) => isAppError(error) && error.kind === 'forbidden',
    },
    // Never auto-retry a non-idempotent write.
    mutations: { retry: false },
  },
});
